import React, { useEffect, useState } from 'react';
import { C, Card, Btn, Alert } from './UI';

interface CancellationPolicy {
    canCancel: boolean;
    refundAmount: number;
    cancellationFee: number;
    policyMessage: string;
}

interface Props {
    jobId: string;
    onConfirm: (reason: string) => void;
    onClose: () => void;
}

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const CANCEL_REASONS = [
    'Changed my mind',
    'Scheduled wrong time',
    'No longer need the service',
    'Found another solution',
    'Other'
];

export function CancelJobModal({ jobId, onConfirm, onClose }: Props) {
    const [policy, setPolicy] = useState<CancellationPolicy | null>(null);
    const [reason, setReason] = useState('');
    const [customReason, setCustomReason] = useState('');
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchPolicy = async () => {
            try {
                const token = localStorage.getItem('br_token');
                const res = await fetch(`${API}/api/jobs/${jobId}/cancellation-policy`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) setPolicy(await res.json());
            } catch {
                setError('Could not load cancellation policy.');
            } finally {
                setFetching(false);
            }
        };
        fetchPolicy();
    }, [jobId]);

    const handleConfirm = async () => {
        const finalReason = reason === 'Other' ? customReason : reason;
        if (!finalReason.trim()) { setError('Please select a reason.'); return; }
        setLoading(true);
        setError('');
        try {
            onConfirm(finalReason);
        } catch (e: any) {
            setError(e.message);
            setLoading(false);
        }
    };

    return (
        <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: 24
        }}>
            <Card style={{ width: '100%', maxWidth: 420 }}>
                <h3 style={{ margin: '0 0 6px', fontSize: 16 }}>Cancel pickup</h3>

                {fetching ? (
                    <div style={{ color: C.textSub, fontSize: 13, padding: '20px 0' }}>
                        Loading cancellation policy...
                    </div>
                ) : policy ? (
                    <>
                        {/* Refund policy banner */}
                        <div style={{
                            background: policy.canCancel ? C.greenBg : '#FFF0F0',
                            border: `1px solid ${policy.canCancel ? C.greenLight : '#F09595'}`,
                            borderRadius: 8, padding: 14, marginBottom: 20
                        }}>
                            <div style={{
                                fontSize: 13, fontWeight: 600,
                                color: policy.canCancel ? C.green : '#9B1C1C',
                                marginBottom: 4
                            }}>
                                {policy.canCancel ? '✓ Cancellation available' : '✗ Cannot cancel'}
                            </div>
                            <div style={{ fontSize: 12, color: C.textSub }}>
                                {policy.policyMessage}
                            </div>
                            {policy.canCancel && (
                                <div style={{ marginTop: 10, display: 'flex', gap: 20 }}>
                                    <div>
                                        <div style={{ fontSize: 11, color: C.textMuted }}>Refund</div>
                                        <div style={{ fontSize: 16, fontWeight: 700, color: C.green }}>
                                            ${policy.refundAmount.toFixed(2)}
                                        </div>
                                    </div>
                                    {policy.cancellationFee > 0 && (
                                        <div>
                                            <div style={{ fontSize: 11, color: C.textMuted }}>Fee</div>
                                            <div style={{ fontSize: 16, fontWeight: 700, color: '#9B1C1C' }}>
                                                ${policy.cancellationFee.toFixed(2)}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {policy.canCancel && (
                            <>
                                <Alert message={error} />
                                <div style={{ marginBottom: 16 }}>
                                    <label style={{ fontSize: 12, fontWeight: 500, color: C.textSub, display: 'block', marginBottom: 8 }}>
                                        Reason for cancellation
                                    </label>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {CANCEL_REASONS.map(r => (
                                            <label key={r} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                                                <input
                                                    type="radio"
                                                    name="reason"
                                                    value={r}
                                                    checked={reason === r}
                                                    onChange={() => setReason(r)}
                                                    style={{ accentColor: C.greenMid }}
                                                />
                                                <span style={{ fontSize: 13, color: C.text }}>{r}</span>
                                            </label>
                                        ))}
                                    </div>
                                    {reason === 'Other' && (
                                        <textarea
                                            value={customReason}
                                            onChange={e => setCustomReason(e.target.value)}
                                            placeholder="Please describe your reason..."
                                            rows={3}
                                            style={{
                                                marginTop: 10, width: '100%', padding: '10px 12px',
                                                border: `1px solid ${C.border}`, borderRadius: 8,
                                                fontSize: 13, fontFamily: 'Inter, sans-serif',
                                                resize: 'vertical', boxSizing: 'border-box' as const
                                            }}
                                        />
                                    )}
                                </div>
                                <div style={{ display: 'flex', gap: 10 }}>
                                    <Btn variant="outline" onClick={onClose}>Keep booking</Btn>
                                    <Btn
                                        variant="danger"
                                        onClick={handleConfirm}
                                        disabled={loading || !reason}
                                        fullWidth
                                    >
                                        {loading ? 'Cancelling...' : 'Confirm cancellation'}
                                    </Btn>
                                </div>
                            </>
                        )}

                        {!policy.canCancel && (
                            <Btn fullWidth onClick={onClose}>Close</Btn>
                        )}
                    </>
                ) : (
                    <>
                        <Alert message={error} />
                        <Btn fullWidth onClick={onClose}>Close</Btn>
                    </>
                )}
            </Card>
        </div>
    );
}
